import { baueBausteinbaum, type Bausteinzeile } from '../domain/bausteine';
import type { SnippetNode } from '../domain/types';
import type { Store } from './port';
import type {
  ExamItemContainerPredefinedCommentPhraseListRow,
  ExamItemPredefinedCommentPhraseListRow,
  PredefinedCommentPhraseRow,
} from './rows';

/**
 * Die Bausteine einer Aufgabe, aus der Ablage.
 *
 * Sie kommen mit der Prüfung herein (`server/classic/abbildung.ts::baueBausteine`) und stehen
 * seither in drei Tabellen, die bisher **niemand las** — die Leiste im Kommentarblatt hing an
 * den Beispieldaten. Seit dem 2026-09-16 zeigt sie, was der Server wirklich geliefert hat.
 *
 * ⚠️ **Eine Aufgabe kann mehrere Listen führen.** Relay verknüpft Liste und Aufgabe über eine
 * eigene Tabelle, und nichts verbietet zwei Einträge. Sie werden hintereinandergehängt, statt
 * die erste zu nehmen: eine stillschweigend unterschlagene Liste wäre ein halber Baum, dem man
 * nicht ansieht, dass er halb ist.
 */
export async function ladeBausteine(store: Store, examItemId: string): Promise<SnippetNode[]> {
  const verknuepfungen = await store.all<ExamItemPredefinedCommentPhraseListRow>(
    'ExamItemPredefinedCommentPhraseList',
    { examItemID: examItemId },
  );
  return baumAusListen(store, verknuepfungen);
}

/**
 * Die Bausteine einer **Station**.
 *
 * ⭐ **Berichtigung durch die Relay-Seite am 2026-09-20.** Die Station führt eine eigene Liste
 * (`ExamItemContainer.textBlockContainerID`); der Auftrag des Nutzers vom 2026-09-18 lautete
 * „zuordenbar zu Station **und** Aufgabe". Unser Papier ging davon aus, beim Stationskommentar
 * gebe es keine Bausteine, weil dort keine Aufgabe steht — eine gepflegte Liste bliebe damit
 * unsichtbar.
 *
 * ⚠️ **Eigene Tabelle, nicht dieselbe.** In `ExamItemPredefinedCommentPhraseList` zeigt
 * `examItemID` auf `ExamItem` und ist `NOT NULL`. Eine Stationskennung dort einzutragen hiesse,
 * die Spalte zu belügen — und auf dem Gerät schlüge der Fremdschlüssel zu.
 */
export async function ladeStationsbausteine(store: Store, stationId: string): Promise<SnippetNode[]> {
  const verknuepfungen = await store.all<ExamItemContainerPredefinedCommentPhraseListRow>(
    'ExamItemContainerPredefinedCommentPhraseList',
    { examItemContainerID: stationId },
  );
  return baumAusListen(store, verknuepfungen);
}

/** Aus den verknüpften Listen den Baum bauen — für beide Bezüge derselbe Weg. */
async function baumAusListen(
  store: Store,
  verknuepfungen: readonly { predefinedCommentPhraseListID: string }[],
): Promise<SnippetNode[]> {
  if (verknuepfungen.length === 0) return [];

  const zeilen: Bausteinzeile[] = [];
  for (const v of verknuepfungen) {
    const rohe = await store.all<PredefinedCommentPhraseRow>('PredefinedCommentPhrase', {
      predefinedCommentPhraseListID: v.predefinedCommentPhraseListID,
    });
    for (const r of rohe) {
      zeilen.push({
        id: r.id,
        parentPredefinedCommentPhraseID: r.parentPredefinedCommentPhraseID,
        name: r.name,
        fullText: r.fullText,
        orderNo: r.orderNo,
      });
    }
  }

  return baueBausteinbaum(zeilen);
}
